'use client';
import { AuthViews } from '@repo/ui/src/modules/UI/constants';
import LanguageSelector from '../LanguageSelector';
import MobileSecondaryNav from './_MobileSecondaryNav';
import NavDropdown, { NavDropdownProps } from './_NavDropdown';
import { NavItemProps } from './index';
import { Anchor } from '@repo/ui/src/components/Anchor';
import AccountIcon from '@repo/ui/src/components/Icons/AccountIcon';
import Image from '@repo/ui/src/components/Image';
import usePrevious from '@repo/ui/src/hooks/usePrevious';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { LinkProps } from '@repo/ui/src/types';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import FocusTrap from 'focus-trap-react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { appConfig } from '../../utils/appConfig';

export interface MobileNavProps {
  open: boolean;
  offset: number;
  navItems: Array<NavItemProps>;
  regions: Array<Region>;
  searchLink: LinkProps;
  dealerLocator?: LinkProps;
  account?: {
    greeting?: string;
    loggedInLabel?: string;
    loggedOutText: string;
    link?: LinkProps;
  };
  // Hooks passed as props for flexibility
  useCustomer?: () => { data: any };
  useLogout?: () => { trigger: () => void };
}

const componentTypes = {
  'secondaryNav.json': MobileSecondaryNav,
  'navDropdown.json': NavDropdown,
};

const MobileNav = ({
  open,
  offset,
  navItems,
  searchLink,
  regions,
  account,
  dealerLocator,
  useCustomer,
  useLogout,
}: MobileNavProps) => {
  const { t } = useTranslation();
  const { data: customer } = useCustomer?.() || { data: null };
  const { trigger: logout } = useLogout?.() || { trigger: () => {} };
  const [activeDropdown, setActiveDropdown] = React.useState<NavDropdownProps | null>(null);
  const [activeNav, setActiveNav] = React.useState<NavItemProps | null>(null);
  const [visible, setVisible] = React.useState(open);
  const previousDropdown = usePrevious(activeDropdown);
  const { push } = useModal(ModalID.login);

  React.useEffect(() => {
    setVisible(true);
    if (!open) {
      setActiveDropdown(null);
    }
  }, [open]);

  return (
    <nav
      data-testid="mobile-nav"
      aria-label="Main menu"
      className={classNames(
        'fixed inset-0 sm:hidden row-start-2 transition-transform duration-500 -z-10 max-h-screen flex flex-col',
        appConfig.isTraeger ? 'bg-warm-100' : 'bg-neutral-100',
        open ? 'translate-y-0' : '-translate-y-full pointer-events-none',
        !visible && 'invisible',
      )}
      onTransitionEnd={() => {
        if (!open) {
          setVisible(false);
        }
      }}
      style={{ paddingTop: `${offset}px` }}
    >
      <section
        className={classNames(
          'grid grid-cols-2 w-[200%] overflow-x-hidden transition-transform duration-500 h-full overflow-y-auto',
          activeDropdown && '-translate-x-1/2',
        )}
      >
        <FocusTrap
          active={open && visible && activeDropdown == null}
          focusTrapOptions={{
            allowOutsideClick: true,
          }}
        >
          <div className="max-h-full overflow-y-auto px-6">
            <Accordion allowZeroExpanded>
              {navItems.map((item, i) => {
                const hasContent = !!item.content;

                if (!hasContent) return null;

                return (
                  <AccordionItem
                    heading={item.title}
                    key={i}
                    uuid={i}
                    headerFontClass="tra-text-web-heading-4"
                    className="!my-0"
                  >
                    <DynamicComponent
                      {...item.content}
                      _componentTypes={componentTypes}
                      searchLink={searchLink}
                      setActiveDropdown={(value: NavDropdownProps | null) => {
                        setActiveDropdown(value);
                        setActiveNav(item);
                      }}
                      activeDropdown={activeDropdown}
                      primaryHeader={item?.title}
                    />
                  </AccordionItem>
                );
              })}
            </Accordion>

            {navItems.map((item, i) => {
              const hasLink = !!item.titleAsLink;
              const hasContent = !!item.content;

              if (!hasLink || hasContent) return null;

              return (
                <div key={`link-${i}`} className="py-6 border-b border-neutral-300 last:border-b-0">
                  <Anchor
                    {...item.titleAsLink}
                    className="tra-text-web-heading-4 text-neutral-700"
                    onClick={() =>
                      navigationClicked({
                        nav_type: 'Top Navigation',
                        clicked_text: item.title ?? '',
                        primary_header: item.title ?? '',
                      })
                    }
                  >
                    {item.title}
                  </Anchor>
                </div>
              );
            })}
            {dealerLocator && (
              <div className="py-6">
                <Anchor
                  {...dealerLocator}
                  className="tra-text-web-heading-4 text-neutral-700"
                  onClick={() =>
                    navigationClicked({
                      nav_type: 'Top Navigation',
                      clicked_text: dealerLocator.displayName ?? '',
                      primary_header: dealerLocator.displayName ?? '',
                    })
                  }
                />
              </div>
            )}
          </div>
        </FocusTrap>
        <FocusTrap
          active={activeDropdown != null}
          focusTrapOptions={{
            allowOutsideClick: true,
          }}
        >
          <div className="px-2" id="mobile-tertiary-nav">
            {(activeDropdown || previousDropdown) && (
              <NavDropdown
                {...((activeDropdown ?? previousDropdown) as NavDropdownProps)}
                setActiveDropdown={setActiveDropdown}
                showMobileHeader
                primaryHeader={activeNav?.title}
                secondaryHeader={activeDropdown?.title}
              />
            )}
          </div>
        </FocusTrap>
      </section>
      <section className="bg-warm-200 py-5 px-6">
        <div className="grid grid-cols-[auto_auto_1fr] gap-x-4 text-neutral-500 items-center">
          {appConfig.isTraeger && (
            <>
              {customer ? (
                <div className={classNames('relative w-6 h-6')}>
                  <Image
                    className="rounded-full"
                    src={customer?.avatar ?? '/icons/account.svg'}
                    alt=""
                    role="presentation"
                    layout="fill"
                  />
                </div>
              ) : (
                <AccountIcon className="w-6" />
              )}
              <div className="flex gap-x-2 items-center">
                {customer ? (
                  <>
                    <Button
                      as={Anchor}
                      aria-label={account?.loggedOutText}
                      label={account?.loggedInLabel}
                      link={t('accountLink.link', undefined, { default: '/account/order-history' })}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      className="capitalize"
                    >
                      {account?.link?.displayName ?? 'View account'}
                    </Button>
                    |
                    <Button
                      aria-label={account?.loggedOutText}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      onClick={() => logout()}
                      className="capitalize"
                    >
                      {t('account.logoutText', undefined, { default: 'Logout' })}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      aria-label={account?.loggedOutText}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      onClick={() => push()}
                      className="capitalize"
                    >
                      {t('account.loginText', undefined, { default: 'Log In' })}
                    </Button>
                    |
                    <Button
                      aria-label={account?.loggedOutText}
                      variant={ButtonVariant.bare}
                      size="none"
                      dark
                      onClick={() => push({ view: AuthViews.CREATE })}
                      className="capitalize"
                    >
                      {t('account.createAccountText', undefined, { default: 'Create Account' })}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
          <div className="justify-self-end">
            <LanguageSelector regions={regions} />
          </div>
        </div>
      </section>
    </nav>
  );
};

export default MobileNav;
